/**
 * Connection Panel UI Component for WebRTC signaling
 */

import type { SignalingData, RTCConnectionState } from '../rtc/types.ts';
import {
  encodeSignalingData,
  decodeSignalingData,
  generateQRCode,
  copyToClipboard,
  validateSignalingData,
} from '../utils/signaling.ts';

export interface ConnectionPanelCallbacks {
  onCreateGame: () => void;
  onJoinGame: (signalingData: SignalingData) => void;
  onCompleteConnection: (signalingData: SignalingData) => void;
}

/**
 * Create connection panel HTML element
 */
export function createConnectionPanel(
  callbacks: ConnectionPanelCallbacks
): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'connection-panel';
  panel.innerHTML = `
    <div class="connection-controls">
      <h2>Connect to Play</h2>
      
      <div class="connection-option">
        <h3>Start New Game</h3>
        <p>Create a new game and share the connection code with your opponent.</p>
        <button id="create-game-btn" class="primary-btn">Create Game</button>
      </div>
      
      <div class="connection-option">
        <h3>Join Game</h3>
        <p>Enter the connection code from your opponent to join their game.</p>
        <div class="input-group">
          <textarea 
            id="join-code-input" 
            placeholder="Paste connection code here..."
            aria-label="Connection code input"
            rows="4"
          ></textarea>
          <button id="join-game-btn">Join Game</button>
        </div>
      </div>
      
      <div id="share-section" class="share-section hidden">
        <h3>Share Connection Code</h3>
        <p>Share this code with your opponent:</p>
        
        <div class="code-display">
          <textarea 
            id="connection-code" 
            readonly 
            aria-label="Connection code to share"
            rows="4"
          ></textarea>
          <button id="copy-code-btn">Copy Code</button>
        </div>
        
        <div class="qr-code-container">
          <img id="qr-code" alt="QR Code for connection" style="display: none;">
          <button id="show-qr-btn">Show QR Code</button>
        </div>
        
        <div id="waiting-indicator" class="status-indicator">
          <span class="spinner"></span>
          Waiting for opponent to connect...
        </div>
      </div>
      
      <div id="answer-section" class="answer-section hidden">
        <h3>Complete Connection</h3>
        <p>Paste the response code from your opponent:</p>
        
        <div class="input-group">
          <textarea 
            id="answer-code-input" 
            placeholder="Paste response code here..."
            aria-label="Response code input"
            rows="4"
          ></textarea>
          <button id="complete-connection-btn">Connect</button>
        </div>
      </div>
      
      <div id="connection-status" class="connection-status">
        <span id="status-text">Not connected</span>
      </div>
    </div>
  `;

  setupConnectionPanelEvents(panel, callbacks);
  return panel;
}

/**
 * Setup event listeners for connection panel
 */
function setupConnectionPanelEvents(
  panel: HTMLElement,
  callbacks: ConnectionPanelCallbacks
): void {
  const createGameBtn = panel.querySelector(
    '#create-game-btn'
  ) as HTMLButtonElement;
  const joinGameBtn = panel.querySelector(
    '#join-game-btn'
  ) as HTMLButtonElement;
  const joinCodeInput = panel.querySelector(
    '#join-code-input'
  ) as HTMLTextAreaElement;
  const copyCodeBtn = panel.querySelector(
    '#copy-code-btn'
  ) as HTMLButtonElement;
  const connectionCodeArea = panel.querySelector(
    '#connection-code'
  ) as HTMLTextAreaElement;
  const showQrBtn = panel.querySelector('#show-qr-btn') as HTMLButtonElement;
  const qrCodeImg = panel.querySelector('#qr-code') as HTMLImageElement;
  const completeConnectionBtn = panel.querySelector(
    '#complete-connection-btn'
  ) as HTMLButtonElement;
  const answerCodeInput = panel.querySelector(
    '#answer-code-input'
  ) as HTMLTextAreaElement;

  // Create game button
  createGameBtn.addEventListener('click', () => {
    callbacks.onCreateGame();
  });

  // Join game button
  joinGameBtn.addEventListener('click', () => {
    const code = joinCodeInput.value.trim();
    if (!code) {
      showError('Please enter a connection code');
      return;
    }

    try {
      const signalingData = decodeSignalingData(code);
      if (!validateSignalingData(signalingData)) {
        throw new Error('Invalid signaling data');
      }
      callbacks.onJoinGame(signalingData);
    } catch {
      showError('Invalid connection code');
    }
  });

  // Copy code button
  copyCodeBtn.addEventListener('click', async () => {
    try {
      await copyToClipboard(connectionCodeArea.value);
      copyCodeBtn.textContent = 'Copied!';
      setTimeout(() => {
        copyCodeBtn.textContent = 'Copy Code';
      }, 2000);
    } catch {
      showError('Failed to copy code');
    }
  });

  // Show QR code button
  showQrBtn.addEventListener('click', async () => {
    if (qrCodeImg.style.display === 'none') {
      try {
        const code = connectionCodeArea.value;
        const signalingData = decodeSignalingData(code);
        const qrCodeDataURL = await generateQRCode(signalingData);
        qrCodeImg.src = qrCodeDataURL;
        qrCodeImg.style.display = 'block';
        showQrBtn.textContent = 'Hide QR Code';
      } catch {
        showError('Failed to generate QR code');
      }
    } else {
      qrCodeImg.style.display = 'none';
      showQrBtn.textContent = 'Show QR Code';
    }
  });

  // Complete connection button
  completeConnectionBtn.addEventListener('click', () => {
    const code = answerCodeInput.value.trim();
    if (!code) {
      showError('Please enter the response code');
      return;
    }

    try {
      const signalingData = decodeSignalingData(code);
      if (!validateSignalingData(signalingData)) {
        throw new Error('Invalid signaling data');
      }
      callbacks.onCompleteConnection(signalingData);
    } catch {
      showError('Invalid response code');
    }
  });
}

/**
 * Show connection code for sharing
 */
export function showConnectionCode(
  panel: HTMLElement,
  signalingData: SignalingData
): void {
  const shareSection = panel.querySelector('#share-section') as HTMLElement;
  const connectionCodeArea = panel.querySelector(
    '#connection-code'
  ) as HTMLTextAreaElement;

  const code = encodeSignalingData(signalingData);
  connectionCodeArea.value = code;

  shareSection.classList.remove('hidden');
}

/**
 * Show answer section for completing connection
 */
export function showAnswerSection(panel: HTMLElement): void {
  const answerSection = panel.querySelector('#answer-section') as HTMLElement;
  const shareSection = panel.querySelector('#share-section') as HTMLElement;

  shareSection.classList.add('hidden');
  answerSection.classList.remove('hidden');
}

/**
 * Update connection status
 */
export function updateConnectionStatus(
  panel: HTMLElement,
  state: RTCConnectionState
): void {
  const statusText = panel.querySelector('#status-text') as HTMLElement;
  const waitingIndicator = panel.querySelector(
    '#waiting-indicator'
  ) as HTMLElement;

  switch (state) {
    case 'connecting':
      statusText.textContent = 'Connecting...';
      statusText.className = 'status-connecting';
      break;
    case 'connected':
      statusText.textContent = 'Connected';
      statusText.className = 'status-connected';
      waitingIndicator.style.display = 'none';
      hideConnectionPanel(panel);
      break;
    case 'disconnected':
      statusText.textContent = 'Disconnected';
      statusText.className = 'status-disconnected';
      break;
    case 'failed':
      statusText.textContent = 'Connection failed';
      statusText.className = 'status-failed';
      break;
    default:
      statusText.textContent = 'Not connected';
      statusText.className = '';
  }
}

/**
 * Hide connection panel (when game starts)
 */
export function hideConnectionPanel(panel: HTMLElement): void {
  panel.style.display = 'none';
}

/**
 * Show connection panel
 */
export function showConnectionPanel(panel: HTMLElement): void {
  panel.style.display = 'block';
}

/**
 * Show error message
 */
function showError(message: string): void {
  // Simple error display - could be enhanced with a proper notification system
  alert(message);
}
